/**
 * /nature/discover — Public citizen-science dashboard.
 * Heat-map of observations worldwide + top species + CSV export.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Download, MapPin, Globe, TrendingUp, Users, Sparkles } from "lucide-react";
import "leaflet/dist/leaflet.css";

const PRESETS = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "1 year" },
];

export default function NatureDiscover() {
  const [days, setDays] = useState(90);
  const [summary, setSummary] = useState(null);
  const [heatmap, setHeatmap] = useState(null);

  useEffect(() => {
    setSummary(null); setHeatmap(null);
    Promise.all([
      http.get(`/nature/discover/summary?days=${days}`),
      http.get(`/nature/discover/heatmap?days=${days}&precision=1&limit=1500`),
    ]).then(([s, h]) => { setSummary(s.data); setHeatmap(h.data); })
      .catch(() => { setSummary({ error: true }); setHeatmap({ cells: [] }); });
  }, [days]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="nature-discover-page">
      <div className="overline">SOCIALPULSE · CITIZEN SCIENCE</div>
      <div className="flex items-end justify-between flex-wrap gap-4 mt-2">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl">Discover our planet, together.</h1>
          <p className="mt-3 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Every observation on Social Pulse is a data point. Explore where species are spotted,
            what's trending, and download the raw dataset for research.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.days}
              onClick={() => setDays(p.days)}
              data-testid={`discover-range-${p.days}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${days === p.days ? "bg-[var(--wofy-primary)] text-white border-transparent" : ""}`}
              style={days !== p.days ? { borderColor: "var(--wofy-border)" } : undefined}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <SummaryTile Icon={Sparkles} label="Total observations" value={summary?.total_observations ?? "…"} />
        <SummaryTile Icon={TrendingUp} label={`In last ${days}d`} value={summary?.in_period ?? "…"} />
        <SummaryTile Icon={Globe} label="Unique species" value={summary?.unique_species ?? "…"} />
        <SummaryTile Icon={Users} label="Contributors" value={summary?.unique_observers ?? "…"} />
      </div>

      {/* Heat map */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="overline">WHERE SPECIES ARE BEING OBSERVED</h2>
          <span className="text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
            {heatmap?.cells?.length || 0} regions
          </span>
        </div>
        <HeatMap cells={heatmap?.cells || []} />
      </section>

      {/* Kinds + families + categories */}
      <div className="grid md:grid-cols-3 gap-6 mt-10">
        <section className="wofy-card">
          <div className="overline">BY KIND</div>
          {summary?.kinds?.length ? (
            <ul className="mt-4 space-y-2 text-sm">
              {summary.kinds.map((k) => (
                <li key={k.kind} className="flex items-center gap-3">
                  <span className="capitalize flex-1">{k.kind}</span>
                  <span className="font-display text-xl">{k.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>No data yet.</p>
          )}
        </section>

        <section className="wofy-card">
          <div className="overline">TOP FAMILIES</div>
          {summary?.top_families?.length ? (
            <ul className="mt-4 space-y-2 text-sm">
              {summary.top_families.map((f) => (
                <li key={f.family} className="flex items-center gap-3">
                  <span className="flex-1 truncate">{f.family}</span>
                  <span className="font-display text-xl">{f.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>No taxonomic data yet.</p>
          )}
        </section>

        <section className="wofy-card" data-testid="discover-categories">
          <div className="overline">BY CATEGORY</div>
          {summary?.categories?.length ? (
            <ul className="mt-4 space-y-2 text-sm">
              {summary.categories.slice(0, 12).map((c) => (
                <li key={c.key} className="flex items-center gap-3" data-testid={`discover-cat-${c.key}`}>
                  {c.sample_image ? (
                    <img src={c.sample_image} alt="" className="w-8 h-8 rounded object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded" style={{ background: "var(--wofy-surface)" }} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{c.label}</div>
                    <div className="text-[10px] font-mono uppercase" style={{ color: "var(--wofy-text-soft)" }}>{c.group}</div>
                  </div>
                  <span className="font-display text-xl">{c.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>No category data yet.</p>
          )}
        </section>
      </div>

      {/* CSV export */}
      <section className="wofy-card mt-8">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="overline">FOR RESEARCHERS</div>
            <h3 className="font-semibold mt-1">Download the full dataset</h3>
            <p className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              CSV with GPS, timestamp, species, taxonomy, weather snapshot, and observer handle.
              Includes only observations users have marked public.
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <a
              data-testid="discover-csv-year"
              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/nature/discover/export.csv?days=365`}
              className="wofy-btn-primary text-sm"
              download
            >
              <Download className="w-4 h-4" /> Last year (CSV)
            </a>
            <a
              data-testid="discover-csv-all"
              href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/nature/discover/export.csv?days=3650&limit=50000`}
              className="wofy-btn-ghost text-sm"
              download
            >
              <Download className="w-4 h-4" /> All time
            </a>
          </div>
        </div>
      </section>

      <p className="mt-8 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
        Cite as: SocialPulse® Global Weather & Nature Hub · citizen-science observations · {new Date().getFullYear()}
        {" · "} <Link to="/nature" className="underline">Back to hub</Link>
      </p>
    </div>
  );
}


function SummaryTile({ Icon, label, value }) {
  return (
    <div className="wofy-card !py-5">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <div className="overline text-[10px]">{label}</div>
      </div>
      <div className="font-display text-4xl mt-2">{value}</div>
    </div>
  );
}


function HeatMap({ cells }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      if (cancelled || !containerRef.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current, {
          center: [20, 0], zoom: 2, minZoom: 2, worldCopyJump: true,
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap", maxZoom: 18,
        }).addTo(mapRef.current);
      }
      // Clear previous layer.
      if (layerRef.current) {
        layerRef.current.clearLayers();
      } else {
        layerRef.current = L.layerGroup().addTo(mapRef.current);
      }
      const max = Math.max(1, ...cells.map((c) => c.count));
      cells.forEach((c) => {
        const r = 6 + (c.count / max) * 26;
        const marker = L.circleMarker([c.lat, c.lng], {
          radius: r,
          weight: 1,
          color: "#B25D38",
          fillColor: "#B25D38",
          fillOpacity: Math.min(0.75, 0.25 + (c.count / max) * 0.55),
        });
        const top = (c.top_species || []).slice(0, 3).map((s) => `${s.name} (${s.count})`).join(", ") || "—";
        marker.bindPopup(
          `<div style="font-family:Georgia,serif;font-size:13px;max-width:220px">
            <b>${c.count} observation${c.count !== 1 ? "s" : ""}</b><br/>
            <span style="color:#666;font-size:11px">${c.lat.toFixed(1)}, ${c.lng.toFixed(1)}</span><br/>
            <div style="margin-top:6px"><b>Top species:</b><br/>${top}</div>
            ${c.sample_image ? `<img src="${c.sample_image}" style="width:100%;margin-top:6px;border-radius:6px"/>` : ""}
          </div>`
        );
        marker.addTo(layerRef.current);
      });
    })();
    return () => { cancelled = true; };
  }, [cells]);

  return (
    <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }} data-testid="discover-heatmap">
      <div ref={containerRef} className="w-full h-[520px]" style={{ background: "var(--wofy-surface)" }} />
      {cells.length === 0 && (
        <div className="p-6 text-center text-sm flex items-center justify-center gap-2" style={{ color: "var(--wofy-text-soft)" }}>
          <MapPin className="w-4 h-4" /> Not enough public observations yet — invite others to share!
        </div>
      )}
    </div>
  );
}
